import { Add, ArrowDown, ArrowUp, Trash } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getCustomSurveySectionElementId } from '../builderUtils';
import { QuestionCard } from './QuestionCard';
import type { BuilderAction } from '../builderReducer';
import type { SurveySection } from '@/api/surveys';
import type { ChangeEvent, Dispatch } from 'react';

interface SectionCardProps {
    activeQuestionId: string | null;
    dispatch: Dispatch<BuilderAction>;
    onAddQuestion: (sectionId: string) => void;
    onDeleteSection: (sectionId: string) => void;
    questionNumberOffset: number;
    section: SurveySection;
    sectionIndex: number;
    totalSections: number;
}

export function SectionCard({
    activeQuestionId,
    dispatch,
    onAddQuestion,
    onDeleteSection,
    questionNumberOffset,
    section,
    sectionIndex,
    totalSections,
}: SectionCardProps) {
    const canDeleteSection = totalSections > 1;

    return (
        <section
            id={getCustomSurveySectionElementId(section.id)}
            className="scroll-mt-28 rounded-3xl border border-border bg-card p-4 shadow-sm sm:p-5"
        >
            <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="grid min-w-0 flex-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                        <Label>Título da seção</Label>
                        <Input
                            value={section.title}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                dispatch({
                                    type: 'updateSection',
                                    sectionId: section.id,
                                    patch: { title: event.target.value },
                                })
                            }
                            placeholder={`Seção ${sectionIndex + 1}`}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>Descrição</Label>
                        <Input
                            value={section.description}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                dispatch({
                                    type: 'updateSection',
                                    sectionId: section.id,
                                    patch: { description: event.target.value },
                                })
                            }
                            placeholder="Opcional"
                        />
                    </div>
                </div>

                <div className="flex shrink-0 flex-wrap items-center gap-2 rounded-2xl bg-surface/40 p-2">
                    <TooltipProvider delayDuration={150}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    aria-label="Mover seção para cima"
                                    onClick={() =>
                                        dispatch({
                                            type: 'moveSection',
                                            sectionId: section.id,
                                            direction: 'up',
                                        })
                                    }
                                    disabled={sectionIndex === 0}
                                >
                                    <ArrowUp className="size-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Mover seção para cima</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    aria-label="Mover seção para baixo"
                                    onClick={() =>
                                        dispatch({
                                            type: 'moveSection',
                                            sectionId: section.id,
                                            direction: 'down',
                                        })
                                    }
                                    disabled={sectionIndex === totalSections - 1}
                                >
                                    <ArrowDown className="size-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Mover seção para baixo</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label="Excluir seção"
                        className="text-destructive hover:text-destructive"
                        onClick={() => onDeleteSection(section.id)}
                        disabled={!canDeleteSection}
                    >
                        <Trash className="size-4" />
                    </Button>
                </div>
            </div>

            <div className="space-y-3">
                {section.questions.map((question, questionIndex) => (
                    <QuestionCard
                        key={question.id ?? question.field_name}
                        canMoveDown={questionIndex < section.questions.length - 1}
                        canMoveUp={questionIndex > 0}
                        dispatch={dispatch}
                        isActive={question.id === activeQuestionId}
                        question={question}
                        questionNumber={questionNumberOffset + questionIndex + 1}
                    />
                ))}
            </div>

            {section.questions.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-brand/30 bg-brand-subtle/40 p-6 text-center">
                    <Button type="button" onClick={() => onAddQuestion(section.id)}>
                        <Add className="size-4" />
                        Adicionar primeira pergunta
                    </Button>
                </div>
            ) : (
                <Button
                    type="button"
                    variant="surface"
                    className="mt-4 w-full border-dashed"
                    onClick={() => onAddQuestion(section.id)}
                >
                    <Add className="size-4" />
                    Adicionar pergunta nesta seção
                </Button>
            )}
        </section>
    );
}
